import { diffLines, type Change } from 'diff';
import { T, Var, useGT } from 'gt-react';

const SIDES = ['before', 'after'] as const;

/** Exact line comparison: whitespace inside JSON string values is data. */
export function WorkflowJsonDiffView({
  before,
  after,
  baseVersion,
  targetVersion,
}: {
  before: string;
  after: string;
  baseVersion: number | undefined;
  targetVersion: number;
}) {
  const gt = useGT();
  const occurrences = new Map<string, number>();
  const changes = diffLines(before, after).map((change) => {
    const signature = JSON.stringify([
      change.added ?? false,
      change.removed ?? false,
      change.value,
    ]);
    const occurrence = occurrences.get(signature) ?? 0;
    occurrences.set(signature, occurrence + 1);
    return { change, key: `${signature}:${occurrence}` };
  });
  return (
    <div className="grid max-h-[65vh] grid-cols-2 overflow-auto rounded-md border">
      {SIDES.map((side) => (
        <div key={side} className="min-w-0 border-r last:border-r-0">
          <p className="sticky top-0 border-b bg-muted px-2 py-1 text-xs font-medium">
            {side === 'before' ? (
              <T context="workflow JSON diff baseline column">
                Before · v<Var>{baseVersion}</Var>
              </T>
            ) : (
              <T context="workflow JSON diff target column">
                After · v<Var>{targetVersion}</Var>
              </T>
            )}
          </p>
          <pre
            aria-label={
              side === 'before'
                ? gt('Before JSON', { context: 'workflow JSON diff column' })
                : gt('After JSON', { context: 'workflow JSON diff column' })
            }
            className="overflow-auto p-2 text-xs"
          >
            {changes.map(({ change, key }) => {
              if (
                (side === 'before' && change.added) ||
                (side === 'after' && change.removed)
              )
                return null;
              return <JsonDiffPart key={key} change={change} />;
            })}
          </pre>
        </div>
      ))}
    </div>
  );
}

function JsonDiffPart({ change }: { change: Change }) {
  const Tag = change.removed ? 'del' : change.added ? 'ins' : 'span';
  const className = change.removed
    ? 'bg-destructive/20 decoration-destructive'
    : change.added
      ? 'bg-emerald-500/20 underline decoration-emerald-600'
      : undefined;
  return <Tag className={className}>{change.value}</Tag>;
}
